import { Theme } from "@material-ui/core/styles/createMuiTheme";
import makeStyles from "@material-ui/styles/makeStyles";
import { Relation } from "graphql/types/globalTypes";
import { dataLayerTracking } from "gtmTracking";
import fetchUtil from "helpers/fetch";
import { useAuth } from "hooks/authHooks";
import dynamic from "next/dynamic";
import Head from "next/head";
import React, { useEffect, useState } from "react";
import { SchemaMarkup } from "SchemaMarkup";
import { eventTracking } from "clevertapTracking";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import {
  getPatientAttributes,
  getPatientDetails,
  isBrowser,
  signInWithGoogle,
} from "helpers/commonHelpers";
import SeoContent from "components/SeoContent/SeoContent";
import axios from "axios";
import useMobileDetect from "helpers/useMobileDetect";
import { SignedinState } from "components/enums/SignedinState";
import {
  HOME_PRELOAD_IMG_MOBILE,
  HOME_PRELOAD_IMG_DESKTOP,
} from "../helpers/constants";
import { PartnerSuccessLoginMessage, TIMEOUT } from "helpers/constants";
import AphDialog from "components/web-ui-components/AphDialog";
import AphButton from "components/web-ui-components/AphButton";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import HeroBanner from "components/HeroBanner/HeroBanner";
import PatientsOverview from "components/PatientsOverview/PatientsOverview";

import detectDevice from "helpers/detectDevice";
import { ParsedDeviceCharacterstics } from "components/interfaces/ParsedDeviceCharacterstics";
import { getPromotionEventsObject, isInViewport } from "helpers/gtmUtils";

const DynamicCircleSlider = dynamic(
  () =>
    import("components/CircleSlider").then(
      (defaultComponent) => defaultComponent.default
    ),
  { ssr: false }
);

const DynamicProHealthLife = dynamic(
  () =>
    import("components/ProHealthLife/ProHealthLife").then(
      (defaultComponent) => defaultComponent.default
    ),
  { ssr: false }
);

const DynamicManageProfile = dynamic(
  () =>
    import("components/ManageProfile").then(
      (defaultComponent) => defaultComponent.ManageProfile
    ),
  { ssr: false }
);

const DynamicCircleRenewal = dynamic(
  () =>
    import("components/CircleRenewal").then(
      (defaultComponent) => defaultComponent.CircleRenewal
    ),
  { ssr: false }
);

const DynamicBlogSection = dynamic(
  () =>
    import("components/BlogSection/BlogSection").then(
      (defaultComponent) => defaultComponent.default
    ),
  { ssr: false }
);

const DynamicVaccinationSection = dynamic(
  () =>
    import("components/VaccinationSection/VaccinationSection").then(
      (defaultComponent) => defaultComponent.default
    ),
  { ssr: false }
);

const DynamicAskApollo = dynamic(
  () =>
    import("components/Doctors/AskApolloCta").then(
      (defaultComponent) => defaultComponent.AskApollo
    ),
  { ssr: true }
);

const DynamicSymptomChecker = dynamic(
  () =>
    import("components/SymptomChecker/SymptomChecker").then(
      (defaultComponent) => defaultComponent.SymptomChecker
    ),
  { ssr: true }
);

const useStyles = makeStyles((theme: Theme) => {
  return {
    welcome: {
      paddingBottom: 0,
      paddingTop: 88,
      [theme.breakpoints.down("xs")]: {
        paddingTop: 70,
      },
      [theme.breakpoints.down(900)]: {
        paddingBottom: 55,
      },
    },
    container: {
      maxWidth: 1064,
      margin: "auto",
    },
    pageContainer: {
      overflow: "hidden",
      [theme.breakpoints.up("sm")]: {
        backgroundColor: "#f7f8f5",
      },
    },
    pageContent: {
      padding: "0 20px 20px 20px",
      [theme.breakpoints.up("sm")]: {
        padding: "5px 40px 40px 40px",
      },
    },
    circleSliderContainer: {
      minHeight: 250,
      [theme.breakpoints.down("sm")]: {
        minHeight: 180,
      },
    },
    prohealthContainer: {
      minHeight: 90,
      [theme.breakpoints.down("sm")]: {
        minHeight: 70,
      },
    },
    vaccineContainer: {
      minHeight: 150,
      [theme.breakpoints.down("sm")]: {
        minHeight: 180,
      },
    },
    topPadding0: {
      [theme.breakpoints.down("sm")]: {
        paddingTop: "1px !important",
      },
    },

    topPadding: {
      [theme.breakpoints.down("sm")]: {
        padding: "20px",
      },
    },
  };
});

interface WelcomeProps {
  footerLinks: {};
  seoContent: any;
  staticContentInfo: object;
  isOnWeb: boolean;
  isOnMweb: boolean;
  isRouterFromPayCare: boolean;
  ua: ParsedDeviceCharacterstics;
}

const Welcome: React.FC<WelcomeProps> = (props) => {
  const classes = useStyles({});
  const { isSignedIn, isPartnerLogin, setIsPartnerLogin } = useAuth();
  const { footerLinks, seoContent } = props;
  const { allCurrentPatients, currentPatient } = useAuth();
  const currentDevice = useMobileDetect();
  const onePrimaryUser =
    allCurrentPatients &&
    allCurrentPatients.filter((x) => x.relation === Relation.ME && x?.uhid)
      .length === 1;

  const structuredJSON = {
    "@context": "https://schema.org/",
    "@type": "WebSite",
    name: "Apollo 24*7",
    url: "https://www.apollo247.com/",
  };
  const metaTagProps = {
    title: "Apollo 247 - Online Doctor Consultation & Book Lab Tests at Home",
    description:
      "Apollo 24|7 helps you get treated from Apollo certified doctors at any time of the day, wherever you are. The mobile app has features like e-consultation in 15 minutes, online pharmacy to doorstep delivery of medicines, home diagnostic test and digital vault where you can upload all your medical history.",
    canonicalLink: "https://www.apollo247.com/",
    ogtitle: "Apollo 247 - Online Doctor Consultation & Book Lab Tests at Home",
    ogdescription:
      "Apollo 24|7 helps you get treated from Apollo certified doctors at any time of the day, wherever you are. The mobile app has features like e-consultation in 15 minutes, online pharmacy to doorstep delivery of medicines, home diagnostic test and digital vault where you can upload all your medical history.",
    ogurl: "https://www.apollo247.com/",
    ogsite_name: "Apollo 24|7",
    ogimage: `${process.env.NEXT_PUBLIC_247_CDN_URL}/images/img-doctors@1x.jpg`,
  };

  const [isUserCircle, setIsUserCircle] = useState<boolean>(false);
  const [isExpired, setIsExpired] = useState<boolean>(false);
  const [isUserCare, setIsUserCare] = useState<boolean>(false);
  const [isUserFromApp, setUserFromApp] = useState(false);
  const [partnerLoginMessage, setPartnerLoginMessage] = useState<string>("");
  const [banners, setBanners] = useState<any>([]);
  const [isBannerSeen, setIsBannerSeen] = useState(false);

  const handleScrollable = (e) => {
    if (
      isInViewport(document.querySelector("#slickContainer")) &&
      !isBannerSeen
    ) {
      const activeItemIndex = document.getElementsByClassName(
        "slick-active"
      )[0] as HTMLElement;
      const creative_name = banners[activeItemIndex.dataset.index]?.banner
        ?.split("/")
        .slice(-1)[0]
        .split(".")[0];
      const promotion_id = banners[activeItemIndex.dataset.index]?.banner;
      const event = getPromotionEventsObject(
        "view_promotion",
        "Apollo247 HomePage",
        "Apollo247 HomePage",
        creative_name,
        Number(activeItemIndex.dataset.index) + 1,
        "NA",
        promotion_id
      );
      dataLayerTracking(event);
      window.removeEventListener("scroll", handleScrollable);
      setIsBannerSeen(true);
    }
  };

  useEffect(() => {
    return () => {
      window.removeEventListener("scroll", handleScrollable);
    };
  }, []);

  useEffect(() => {
    if (banners.length) {
      if (isInViewport(document.querySelector("#slickContainer"))) {
        const creative_name = banners[0]?.banner
          ?.split("/")
          .slice(-1)[0]
          .split(".")[0];
        const promotion_name = banners[0]?.banner;
        const event = getPromotionEventsObject(
          "view_promotion",
          "Apollo247 HomePage",
          "Apollo247 HomePage",
          creative_name,
          1,
          "NA",
          promotion_name
        );
        dataLayerTracking(event);
        setIsBannerSeen(true);
      } else {
        window.addEventListener("scroll", handleScrollable);
      }
    }
  }, [banners]);

  useEffect(() => {
    const search = isBrowser() && window?.location?.search;
    const params = new URLSearchParams(search);
    params.get("user_from_device") === "true" && setUserFromApp(true);
    /**Gtm code start start */
    dataLayerTracking({
      event: "pageviewEvent",
      pageURL: window.location.href,
      pageName: "Home Page",
      pageLOB: "Home",
      pageType: "Others",
    });
    /**Gtm code start end */

    sessionStorage.removeItem("nonLoginUserData");
    localStorage.removeItem("testOrderId");
    const userSubscriptions: any = localStorage.getItem("userSubscriptions")
      ? JSON.parse(localStorage.getItem("userSubscriptions"))
      : {};
    const circleSubs =
      userSubscriptions["APOLLO"] &&
      userSubscriptions["APOLLO"].filter(
        (subscription) => subscription.name === "CIRCLE PLAN"
      );
    const inactiveSubscriptions: any = localStorage.getItem(
      "inactiveSubscriptions"
    )
      ? JSON.parse(localStorage.getItem("inactiveSubscriptions"))
      : {};
    setIsUserCircle(circleSubs && circleSubs.length > 0);
    inactiveSubscriptions &&
      inactiveSubscriptions.APOLLO &&
      inactiveSubscriptions.APOLLO.length > 0 &&
      inactiveSubscriptions.APOLLO.map((item) => {
        if (item.status == "disabled") {
          if (circleSubs && circleSubs.length == 0) {
            setIsExpired(true);
          }
        }
      });
  }, []);

  useEffect(() => {
    if (isSignedIn === SignedinState.SIGNEDIN || currentPatient?.id) {
      const userSubscriptions: any = localStorage.getItem("userSubscriptions")
        ? JSON.parse(localStorage.getItem("userSubscriptions"))
        : {};
      const circleSubs =
        userSubscriptions["APOLLO"] &&
        userSubscriptions["APOLLO"].filter(
          (subscription) => subscription.name === "CIRCLE PLAN"
        );

      const patientDetails = getPatientDetails(currentPatient);
      if (circleSubs && Array.isArray(circleSubs) && circleSubs.length > 0) {
        setIsUserCare(true);
      }
      eventTracking("Home page viewed", {
        ...getPatientAttributes(patientDetails),
      });

      const sessionHome =
        sessionStorage.getItem("homeVisit") &&
        JSON.parse(sessionStorage.getItem("homeVisit"));
      if (!sessionHome) {
        sessionStorage.setItem("homeVisit", "true");
        eventTracking("Apollo Homepage Opened", {
          ...getPatientAttributes(patientDetails),
        });
      }
    }
  }, [currentPatient?.id, isSignedIn]);

  useEffect(() => {
    setTimeout(() => {
      if (
        !localStorage.getItem("email_CT") &&
        currentDevice.isDesktop() &&
        isSignedIn === SignedinState.SIGNEDOUT &&
        Number(process.env.NEXT_PUBLIC_ENABLE_SIGN_IN_WITH_GOOGLE_POP_UP)
      ) {
        signInWithGoogle();
      }
    }, TIMEOUT.GOOGLE_SIGN_IN_POP_UP_TIMEOUT);
  }, []);

  useEffect(() => {
    if (isPartnerLogin) {
      setPartnerLoginMessage(PartnerSuccessLoginMessage);
    }
  }, [isPartnerLogin]);

  return (
    <div
      id="mainContainerCT"
      className={`${classes.welcome} ${
        isUserFromApp ? classes.topPadding0 : ""
      }`}
    >
      <Head>
        <title>{metaTagProps.title}</title>
        <meta
          name="facebook-domain-verification"
          content={process.env.FACEBOOK_DOMAIN_VERIFICATION_ID}
        />
        <meta name="description" content={metaTagProps.description} />
        <link rel="canonical" href={metaTagProps.canonicalLink} />
        {currentDevice.isMobile() ? (
          <link rel="preload" as="image" href={`${HOME_PRELOAD_IMG_MOBILE}`} />
        ) : (
          <link rel="preload" as="image" href={`${HOME_PRELOAD_IMG_DESKTOP}`} />
        )}
        <meta property="og:title" content={metaTagProps.ogtitle} />
        <meta property="og:url" content={metaTagProps.ogurl} />
        <meta property="og:image" content={metaTagProps.ogimage} />
        <meta property="og:site_name" content={metaTagProps.ogsite_name} />
        <meta property="og:description" content={metaTagProps.ogdescription} />
        {structuredJSON && <SchemaMarkup structuredJSON={structuredJSON} />}
      </Head>
      <div className={classes.container}>
        <div className={classes.pageContainer}>
          <HeroBanner isUserCare={isUserCare} />
          {isSignedIn === SignedinState.SIGNEDIN && (
            <DynamicCircleRenewal setIsUserCare={setIsUserCare} />
          )}
          <div className={classes.pageContent}>
            <div className={classes.circleSliderContainer}>
              <DynamicCircleSlider banners={banners} setBanners={setBanners} />
            </div>
            <DynamicSymptomChecker
              ua={props?.ua}
              staticContentInfo={props?.staticContentInfo}
              isOnWeb={props?.isOnWeb}
              isOnMweb={props?.isOnMweb}
            />
            {isSignedIn === SignedinState.SIGNEDIN && <PatientsOverview />}
            <DynamicVaccinationSection />
            <DynamicProHealthLife />
            <DynamicBlogSection />
          </div>
          <SeoContent seoData={seoContent?.description} />
        </div>
      </div>
      {!onePrimaryUser && <DynamicManageProfile />}
      <AphDialog open={isPartnerLogin && isSignedIn === SignedinState.SIGNEDIN}>
        <DialogContent>
          <DialogContentText>{partnerLoginMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <AphButton onClick={() => setIsPartnerLogin(false)} color="primary">
            Close
          </AphButton>
        </DialogActions>
      </AphDialog>
    </div>
  );
};

export default Welcome;

export const getServerSideProps = async ({ req, query }) => {
  const api = `${process.env.NEXT_PUBLIC_CMS_BASE_URL}/api/seo-home-html/apollo247home`;
  const ua = detectDevice(req?.headers);
  const isRouterFromPayCare = req?.headers?.referer?.includes("pay-care")
    ? true
    : false;
  const [seoContent, footerLinks, getStaticContentInfo] = await Promise.all([
    axios
      .get(api, {
        headers: {
          Authorization: process.env.NEXT_PUBLIC_CMS_TOKEN,
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        return response?.data?.data;
      })
      .catch((e) => {
        console.error("Failed to fetch content from CDN", e);
      }),
    fetchUtil(
      `${process.env.NEXT_PUBLIC_247_CDN_URL}/files/footerData.json`,
      "GET",
      {},
      "",
      true
    )
      .then((res: any) => {
        return res?.data;
      })
      .catch((e) => {
        console.error("Failed to Fetch Footer Data", e);
      }),
    axios
      .get(
        `${process.env.NEXT_PUBLIC_247_CDN_URL}/images/${process.env.NEXT_PUBLIC_ASK_APOLLO_CONFIG_FILE}`
      )
      .then((response) => {
        return response?.data;
      })
      .catch((e) => {
        return {};
      }),
  ]);

  return {
    props: {
      footerLinks: footerLinks || null,
      seoContent: seoContent || null,
      staticContentInfo: getStaticContentInfo,
      isOnWeb: !!getStaticContentInfo?.showSymptomCheckerOnWeb,
      isOnMweb: !!getStaticContentInfo?.showSymptomCheckerOnMweb,
      ua,
      isRouterFromPayCare,
      pageLOB: "Home Page",
      pagetype: "Home Page",
      sectionName: "Home Page",
    },
  };
};
